// Rollover daily picks — Desk-style premium cards.
// Shows published rollover accas by tier using the pb-prem-* design system.

import { useRolloverPicks, type RolloverPick } from "../../rollover/useRolloverPicks.ts";
import { fmtKick, fmtOdds } from "../football/_fpkit.tsx";
import "../../styles/powerball-premium.css";

export interface RolloverWeekBriefProps {
  readonly slips?: unknown;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly weekNumber?: number;
}

const TIER_DISPLAY: Record<string, { title: string; tagline: string }> = {
  "2x": { title: "2× Rollover", tagline: "Conservative doubles. Compound slowly." },
  "3x": { title: "3× Rollover", tagline: "The standard chain. Discipline rewarded." },
  "4x": { title: "4× Rollover", tagline: "Aggressive growth. Higher conviction." },
  "5x": { title: "5× Rollover", tagline: "Maximum multiplier. Every leg earned." },
  "6x": { title: "6× Rollover", tagline: "Highest target. Every leg independently supported." },
};

function matchHero(match: string, pick: string): JSX.Element {
  const m = match.match(/^(.+?)\s+(?:vs?\.?|-|@)\s+(.+)$/i);
  if (!m) return <span>{match || "TBD"}</span>;
  const home = m[1]!.trim();
  const away = m[2]!.trim();
  const p = pick.toLowerCase();
  const homePicked = p === "home" || p === home.toLowerCase();
  const awayPicked = p === "away" || p === away.toLowerCase();
  return (
    <span>
      <span className={homePicked ? "pickside" : undefined}>{home}</span>
      <span style={{ opacity: 0.5 }}> vs </span>
      <span className={awayPicked ? "pickside" : undefined}>{away}</span>
    </span>
  );
}

export function RolloverWeekBrief(_props: RolloverWeekBriefProps): JSX.Element {
  const { picks, loading, error } = useRolloverPicks();

  // Group picks by tier
  const tiers = new Map<string, RolloverPick[]>();
  for (const p of picks) {
    const arr = tiers.get(p.tier) ?? [];
    arr.push(p);
    tiers.set(p.tier, arr);
  }
  const tierOrder = ["2x", "3x", "4x", "5x", "6x"];
  const activeTiers = tierOrder.filter((t) => tiers.has(t));

  if (error) {
    return (
      <div className="pb-prem-slip" style={{ opacity: 0.6 }}>
        <div className="pb-prem-slip-meta">Picks unavailable: {error}</div>
      </div>
    );
  }

  if (loading || activeTiers.length === 0) {
    return (
      <div className="pb-prem-slip" style={{ opacity: 0.6 }}>
        <h2 className="pb-prem-slip-title">Rollover Accas</h2>
        <p className="pb-prem-slip-tagline">
          {loading ? "Pipeline scanning…" : "Agents are assembling this week's accas. Picks appear when ready."}
        </p>
      </div>
    );
  }

  return (
    <>
      {activeTiers.map((tier) => {
        const legs = tiers.get(tier) ?? [];
        const combined = legs.reduce((a, l) => a * (l.odds > 1 ? l.odds : 1), 1);
        const display = TIER_DISPLAY[tier] ?? { title: `${tier} Rollover`, tagline: "" };

        return (
          <section key={tier} className="pb-prem-slip">
            <div className="pb-prem-slip-header">
              <div>
                <h2 className="pb-prem-slip-title">{display.title}</h2>
                <p className="pb-prem-slip-tagline">{display.tagline}</p>
              </div>
              <span className="pb-prem-slip-meta">{legs.length} legs · acca</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span className="pb-prem-combined">{fmtOdds(combined)}</span>
              <span className="pb-prem-slip-meta" style={{ color: "var(--sz-success-text, #5fd39a)" }}>LIVE</span>
            </div>

            <div className="pb-prem-legs">
              {legs.map((leg, i) => {
                const ctxParts = [leg.league, leg.kickoff_at ? fmtKick(leg.kickoff_at) : null].filter(Boolean);
                return (
                  <article key={i} className="pb-prem-leg" style={{ animationDelay: `${i * 50}ms` }}>
                    <div className="pb-prem-leg-num">Leg {i + 1}</div>
                    <div className="pb-prem-leg-match">{matchHero(leg.match || "TBD", leg.pick)}</div>
                    {ctxParts.length > 0 && <div className="pb-prem-leg-context">{ctxParts.join(" · ")}</div>}
                    <div className="pb-prem-leg-pickline">
                      {leg.market || "Match Result"} · <b>{leg.pick}</b>
                    </div>
                    <div className="pb-prem-leg-metrics">
                      <span className="pb-prem-leg-odds">{fmtOdds(leg.odds)}</span>
                      {leg.confidence != null && leg.confidence > 0 && (
                        <span className="pb-prem-leg-metric">
                          <span className="lbl">Conf {Math.round(leg.confidence)}</span>
                          <span className="pb-prem-probbar"><span style={{ width: `${Math.min(100, leg.confidence)}%` }} /></span>
                        </span>
                      )}
                    </div>
                    {leg.reasoning && <div className="pb-prem-reason">{leg.reasoning}</div>}
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </>
  );
}
